"use client";

import { memo, useEffect, useRef, useState } from "react";
import { HStack, Box } from "@chakra-ui/react";
import {
  NavbarSearchInput,
  NavbarVoiceSearchAction,
  usePageSearch,
} from "@/features/search";
import { navbarConfig } from "@/shared/navigation";
import { useSystemDesign } from "@/shared/system-design";
import ActionMenuDropdown from "./ActionMenuDropdown";
import useNavbarVisibility from "./hooks/useNavbarVisibility";
import { useUserChoices } from "@/state/globalStore";
import { LogoLinkChakra } from "@/app/public-shell/navigation/LogoLink";
import PrivateWorkspaceSwitcher from "./PrivateWorkspaceSwitcher";

// -----------------------------------
// Component
// -----------------------------------
function PrivateNavbar() {
  const { isMobileDevice } = useSystemDesign();
  const { relevantSearchConfig } = usePageSearch();
  const { activeStudioExportLock } = useUserChoices();
  const [isMobileSearchFocused, setIsMobileSearchFocused] = useState(false);
  const [isDesktopSearchFocused, setIsDesktopSearchFocused] = useState(false);

  // Navbar visibility & mounting logic moved to a small hook for clarity and reuse
  const { mounted, isVisible } = useNavbarVisibility(isMobileDevice);

  // Refs for inline navbar input vs dialog textarea
  const navSearchInputRef = useRef<HTMLInputElement | null>(null);
  const mobileSearchFocusRef = useRef<HTMLDivElement | null>(null);
  const desktopSearchFocusRef = useRef<HTMLDivElement | null>(null);

  const searchbarMdWidth = "64";
  const mobileSearchExpanded = isMobileDevice && isMobileSearchFocused;
  const desktopSearchExpanded = !isMobileDevice && isDesktopSearchFocused;

  function exitMobileSearchFocus() {
    setIsMobileSearchFocused(false);
    navSearchInputRef.current?.blur();
  }

  useEffect(() => {
    if (!mobileSearchExpanded) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      navSearchInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [mobileSearchExpanded]);

  useEffect(() => {
    if (!mobileSearchExpanded) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (mobileSearchFocusRef.current?.contains(target)) {
        return;
      }

      exitMobileSearchFocus();
    }

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [mobileSearchExpanded]);

  // Don't render anything on the server
  if (!mounted) {
    return (
      <div style={{ height: navbarConfig.NAVBAR_HEIGHT_MOBILE * 4 }}></div>
    ); // Placeholder for navbar height (chakra units * 4 = px)
  }

  return (
    <Box>
      <Box
        position="fixed"
        left="0"
        right="0"
        top="0"
        transform={
          isMobileDevice && !navbarConfig.NAVBAR_LOCK && !isVisible
            ? "translateY(-100%)"
            : "translateY(0)"
        }
        transition={`transform ${navbarConfig.NAVBAR_HIDE_TRANSITION_SEC}s ease-in-out`}
        style={{ willChange: "transform" }}
        zIndex="dropdown" // equals 1000 see: https://chakra-ui.com/docs/theming/z-index
        backdropFilter="blur(8px)"
        bg="moveon.shell.bg"
        borderBottomWidth="1px"
        borderColor="moveon.shell.border"
        minH={{
          base: navbarConfig.NAVBAR_HEIGHT_MOBILE,
          md: navbarConfig.NAVBAR_HEIGHT_DESKTOP,
        }}
        px={{
          base: "3",
          md: "4",
          lg: "6",
          xl: "8",
        }}
        gap="3"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        boxShadow="sm"
      >
        {isMobileDevice ? (
          <>
            <HStack flex="1" minW={0}>
              {mobileSearchExpanded ? (
                <HStack
                  ref={mobileSearchFocusRef}
                  flex="1"
                  minW={0}
                  align="center"
                >
                  <Box flex="1" minW={0} display="flex" alignItems="center">
                    <NavbarSearchInput
                      placeholder={`Search ${relevantSearchConfig.title} ...`}
                      inputRef={navSearchInputRef}
                      singleLine={true}
                      disabled={activeStudioExportLock ? true : false}
                      hideInternalClearButton={false}
                      showIdleSearchFiltersButton={false}
                      emptyEndElement={
                        <NavbarVoiceSearchAction
                          disabled={activeStudioExportLock ? true : false}
                        />
                      }
                      onCloseSuccess={exitMobileSearchFocus}
                    />
                  </Box>
                </HStack>
              ) : (
                <>
                  <Box flexShrink={0}>
                    <PrivateWorkspaceSwitcher />
                  </Box>

                  <Box
                    flex="1"
                    ml="1"
                    minW={0}
                    display="flex"
                    alignItems="center"
                  >
                    <NavbarSearchInput
                      placeholder={`Search ${relevantSearchConfig.title} ...`}
                      inputRef={navSearchInputRef}
                      singleLine={true}
                      disabled={activeStudioExportLock ? true : false}
                      onFocusAction={() => {
                        setIsMobileSearchFocused(true);
                      }}
                    />
                  </Box>
                </>
              )}
            </HStack>

            <HStack
              gap="2"
              align="center"
              flexShrink={0}
              minW={0}
              display={mobileSearchExpanded ? "none" : "flex"}
            >
              <Box flexShrink={0}>
                <ActionMenuDropdown />
              </Box>
            </HStack>
          </>
        ) : (
          <HStack flex="1" minW={0} gap={{ md: "4", lg: "6" }} align="center">
            <HStack gap="4" minW={0} flexShrink={0}>
              <LogoLinkChakra whichNavbar="private" />
              <PrivateWorkspaceSwitcher />
            </HStack>

            <HStack flex="1" minW={0} justify="flex-end" gap="2">
              <Box
                ref={desktopSearchFocusRef}
                flex={desktopSearchExpanded ? "1" : "0 1 auto"}
                minW={0}
                maxW={{
                  md: desktopSearchExpanded ? "3xl" : "md",
                  lg: desktopSearchExpanded ? "4xl" : "xl",
                }}
                w={{
                  md: desktopSearchExpanded ? "full" : searchbarMdWidth,
                  lg: desktopSearchExpanded ? "full" : "80",
                }}
                transition="max-width 0.2s ease, width 0.2s ease, flex 0.2s ease"
              >
                <NavbarSearchInput
                  placeholder={`Search ${relevantSearchConfig.title} ...`}
                  inputRef={navSearchInputRef}
                  singleLine={true}
                  disabled={activeStudioExportLock ? true : false}
                  hideInternalClearButton={!desktopSearchExpanded}
                  showIdleSearchFiltersButton={!desktopSearchExpanded}
                  emptyEndElement={
                    desktopSearchExpanded ? (
                      <NavbarVoiceSearchAction
                        disabled={activeStudioExportLock ? true : false}
                      />
                    ) : undefined
                  }
                  onFocusAction={() => {
                    setIsDesktopSearchFocused(true);
                  }}
                  onBlurAction={(event) => {
                    const nextFocused = event.relatedTarget;

                    if (
                      nextFocused instanceof Node &&
                      desktopSearchFocusRef.current?.contains(nextFocused)
                    ) {
                      return;
                    }

                    setIsDesktopSearchFocused(false);
                  }}
                />
              </Box>
            </HStack>

            <Box flexShrink={0}>
              <ActionMenuDropdown />
            </Box>
          </HStack>
        )}
      </Box>
      <Box
        pt={{
          base: navbarConfig.NAVBAR_HEIGHT_MOBILE,
          md: navbarConfig.NAVBAR_HEIGHT_DESKTOP,
        }}
      ></Box>
    </Box>
  );
}

export default memo(PrivateNavbar);
